/**
 * Detect a transient, browser-level fetch failure — i.e. a request that never
 * received an HTTP response (offline, connection dropped, request cancelled by
 * a tab navigating away, or blocked by an extension/ad-blocker).
 *
 * Per the fetch spec a network failure rejects with a `TypeError`; the message
 * wording is browser-specific:
 *   - Chrome / Edge:  "Failed to fetch"
 *   - Firefox:        "NetworkError when attempting to fetch resource."
 *   - Safari/WebKit:  "Load failed"
 *   - undici / Node:  "fetch failed"
 *
 * These are NOT first-party bugs and are not actionable, so callers on
 * non-critical paths may swallow them silently instead of reporting to Sentry.
 *
 * A `RestError` (a received HTTP response that wasn't ok) is deliberately NOT
 * matched: it carries a real status code and may signal a genuine server
 * problem worth reporting.
 */
const NETWORK_ERROR_PATTERNS = [
  'failed to fetch', // Chrome / Edge
  'networkerror', // Firefox: "NetworkError when attempting to fetch resource."
  'load failed', // Safari / WebKit
  'the network connection was lost',
  'the internet connection appears to be offline',
  'fetch failed', // undici / Node
];

export function isNetworkError(err: unknown): boolean {
  if (!(err instanceof TypeError)) return false;
  const message = err.message.toLowerCase();
  return NETWORK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}
